// Copyright 2021-2024, 2026 - Hewlett Packard Enterprise Company

import { useCallback, useMemo, useState, useEffect } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDClient } from 'launchdarkly-react-client-sdk';
import { ldFlagDefaults } from '../constants/ldFlagDefaults';
import { ldFlagOverrides } from '../constants/ldFlagOverrides';

// A LaunchDarkly variation is delivered as JSON, so this is the whole vocabulary.
export type FlagValue =
  boolean | number | string | null | FlagValue[] | { [key: string]: FlagValue };

export type FlagName = keyof typeof ldFlagDefaults;

// LD owns a flag's value; the default only signals its shape, so keep the type
// and discard the literal. An empty-array default infers as `any[]` from the JS
// defaults module — anything LD can send back is JSON.
type Concrete<T> = 0 extends 1 & T
  ? FlagValue
  : T extends boolean
    ? boolean
    : T extends number
      ? number
      : T extends string
        ? string
        : T;
type FlagValueOf<T> = T extends (infer E)[] ? Concrete<E>[] : Concrete<T>;

export type CustomKnownIssue = {
  title?: string;
  symptom?: string;
  cause?: string;
  action?: string;
};

// A default is only a fallback, so for a JSON flag it cannot carry the real
// payload shape. Declare those here; the constraint rejects unknown flag names.
type ShapeOverrides<T extends Partial<Record<FlagName, FlagValue>>> = T;

export type FlagShapes = ShapeOverrides<{
  T_20241022_49238_UI_CUSTOM_KNOWN_ISSUE_JSON:
    CustomKnownIssue | CustomKnownIssue[];
}>;

/**
 * Each flag is a function so that LaunchDarkly records one evaluation per flag
 * actually read, rather than marking every flag evaluated on every render.
 * Keyed by `ldFlagDefaults` — the same contract `indigo/flags-must-have-defaults`
 * enforces — with `FlagShapes` taking precedence over the derived default type.
 */
export type Flags = {
  [K in FlagName]: () => K extends keyof FlagShapes
    ? FlagShapes[K]
    : FlagValueOf<(typeof ldFlagDefaults)[K]>;
};

function featureFlag(
  ldClient: LDClient | undefined,
  flagName: string,
  flagDefault?: FlagValue,
): () => FlagValue {
  return () => {
    if (flagName in ldFlagOverrides) {
      return ldFlagOverrides[flagName as keyof typeof ldFlagOverrides];
    }
    if (ldClient) {
      return ldClient.variation(flagName, flagDefault);
    }
    return flagDefault;
  };
}

export default function useFlags(): Flags {
  const ldClient = useLDClient();
  const [ldFlags, setLdFlags] = useState<Record<string, FlagValue>>(() => {
    if (ldClient) {
      return ldClient.allFlags();
    }
    return {};
  });

  const onFlagsChange = useCallback(() => {
    if (ldClient) {
      setLdFlags(ldClient.allFlags());
    }
  }, [ldClient]);

  useEffect(() => {
    if (ldClient) {
      // if the client receives an event that the LD flags changed, check if any
      // new flag functions need to be added
      ldClient.on('initialized', onFlagsChange);
      ldClient.on('change', onFlagsChange);
    }

    return () => {
      if (ldClient) {
        // remove listeners when unmounting
        ldClient.off('initialized', onFlagsChange);
        ldClient.off('change', onFlagsChange);
      }
    };
  }, [onFlagsChange, ldClient]);

  return useMemo(() => {
    const flags: Record<string, () => FlagValue> = {};

    // create flag wrapper fns for all statically defined flags with defaults
    Object.entries(ldFlagDefaults).forEach(([key, defaultValue]) => {
      flags[key] = featureFlag(ldClient, key, defaultValue);
    });

    // create flag wrapper fns for flags that aren't statically defined in code
    Object.keys(ldFlags).forEach(key => {
      // don't recreate functions that already exist
      if (!(key in flags)) {
        flags[key] = () => featureFlag(ldClient, key)();
      }
    });

    // every ldFlagDefaults key is populated above; the extra runtime-only keys
    // are deliberately left outside the public contract
    return flags as Flags;
  }, [ldFlags, ldClient]);
}
